import {
  IonDatetime,
  IonInput,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonTextarea,
} from '@ionic/react';
import React from 'react';
import { useForm } from 'react-hook-form';

import moment from 'moment';
import 'moment/locale/es';

import { useSiniestros } from '../../../context/SiniestrosContext';
import { usePostSiniestro } from '../../../hooks/Siniestro/useSiniestros';
import { useAlert } from '../../../hooks/UI/useAlert';
import { useEnter } from '../../../hooks/Utils/useEnter';
import { SiniestroReq, siniestroDefaults } from '../../../types/Siniestro/types';
import { getSiniestroCreateErrorMessage } from '../../../utils/apiHealth';
import { isDawerPet } from '../../../utils/coverage';
import { today } from '../../../utils/date';
import { formatDenunciaByTipo, sanitizeDenuncia } from '../../../utils/siniestro';
import { capitalizeFirstLetter } from '../../../utils/string';
import Alert from '../../UI/Alert';
import Button from '../../UI/Button';
import FormItem from '../../UI/FormItem';
import Spinner from '../../UI/Spinner';
import './SiniestroForm.scss';
import { SiniestroFormProps } from './types';

moment.locale('es');

const SiniestroForm: React.FC<SiniestroFormProps> = ({ pet, header, closeModal }) => {
  const { ref, handleEnter } = useEnter();
  const { mutateSiniestros } = useSiniestros();
  const { postSiniestro, isLoading } = usePostSiniestro();
  const { alertState, showAlert, hideAlert } = useAlert();
  const isDawer = isDawerPet(pet);
  const allowVacuna = !isDawer;

  const { control, handleSubmit, errors, watch } = useForm<SiniestroReq>({
    defaultValues: siniestroDefaults,
  });

  const tipoSeleccionado = watch('tipo') || '';
  const isTipoSelected = Boolean(tipoSeleccionado);
  const tipoOptions: Array<{ value: SiniestroReq['tipo']; label: string }> =
    allowVacuna
      ? [
          { value: 'accidente', label: 'Accidente' },
          { value: 'enfermedad', label: 'Enfermedad' },
          { value: 'vacuna', label: 'Vacuna' },
        ]
      : [
          { value: 'accidente', label: 'Accidente' },
          { value: 'enfermedad', label: 'Enfermedad' },
        ];

  const labelFecha =
    tipoSeleccionado === 'vacuna'
      ? 'Fecha de vacunación'
      : tipoSeleccionado === 'accidente'
        ? 'Fecha del accidente'
        : 'Fecha de inicio de síntomas';

  const placeholder =
    tipoSeleccionado === 'vacuna'
      ? `Ejemplo: el ${moment().format('DD/MM/YYYY')} ${pet.nombre} recibió la vacuna antirrábica.`
      : tipoSeleccionado === 'accidente'
        ? `Ejemplo: el ${moment().subtract(2, 'days').format('dddd DD/MM/YYYY')} ${
            pet.nombre
          } tuvo una caída jugando y comenzó a renguear.`
        : `Ejemplo: el ${moment().subtract(7, 'days').format('dddd DD/MM/YYYY')} ${
            pet.nombre
          } empezó a vomitar cada vez que comía.`;

  const onSubmit = async (values: SiniestroReq) => {
    try {
      const tipo = values.tipo;

      if (!allowVacuna && tipo === 'vacuna') {
        showAlert({
          header: 'Error',
          message:
            'Para mascotas Dawer solo se pueden informar accidentes o enfermedades.',
          buttons: [{ text: 'Entendido', role: 'cancel' }],
        });
        return;
      }

      const fechaOk = moment(values.fecha_carga).subtract(1, 'hours').toISOString();

      const siniestro: SiniestroReq = {
        tipo,
        fecha_carga: fechaOk,
        denuncia_administrativa: formatDenunciaByTipo(
          tipo,
          sanitizeDenuncia(values.denuncia_administrativa),
        ),
        mascotaId: String(pet.id),
      };

      const response = await postSiniestro(siniestro);

      if (response.ok) {
        await mutateSiniestros();

        showAlert({
          header: 'Envio exitoso',
          message: 'El caso se agregó correctamente.',
          buttons: [{ text: 'Entendido', role: 'confirm' }],
          onDidDismiss: closeModal,
        });
      } else {
        showAlert({
          header: 'Error',
          message: getSiniestroCreateErrorMessage(response.error),
          buttons: [{ text: 'Reintentar', role: 'cancel' }],
        });
      }
    } catch (error) {
      console.error('Error en onSubmit:', error);
      showAlert({
        header: 'Error',
        message: getSiniestroCreateErrorMessage(error),
        buttons: [{ text: 'Entendido' }],
      });
    }
  };

  return (
    <div className="siniestro-form">
      {header}

      <form
        onSubmit={handleSubmit(onSubmit)}
        onKeyUp={handleEnter}
        className="ion-padding"
      >
        <IonItem className="siniestro-form__pet_input">
          <IonLabel position="stacked">Mascota</IonLabel>
          <IonInput value={pet.nombre} readonly />
        </IonItem>

        <FormItem
          name="tipo"
          control={control}
          errors={errors}
          rules={{
            required: { value: true, message: 'Por favor seleccioná el tipo' },
          }}
          render={({ onChange, value }) => (
            <IonItem>
              <IonLabel position="floating">Tipo de siniestro</IonLabel>
              <IonSelect
                value={value}
                mode="ios"
                interface="action-sheet"
                placeholder="Seleccioná una opción"
                cancelText="Cancelar"
                onIonChange={(e) => onChange(e.detail.value)}
              >
                {tipoOptions.map((option) => (
                  <IonSelectOption key={option.value} value={option.value}>
                    {option.label}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>
          )}
        />

        <FormItem
          name="fecha_carga"
          control={control}
          errors={errors}
          rules={{
            required: { value: true, message: 'Por favor, ingresá la fecha' },
          }}
          render={({ onChange, value }) => (
            <IonItem>
              <IonLabel position="floating">{labelFecha}</IonLabel>
              <IonDatetime
                disabled={!isTipoSelected}
                value={value}
                max={today}
                displayFormat="DD/MM/YYYY"
                doneText="Confirmar"
                cancelText="Cancelar"
                onIonChange={(e) => onChange(e.detail.value)}
              />
            </IonItem>
          )}
        />

        <div className="ion-margin-bottom">
          <FormItem
            name="denuncia_administrativa"
            control={control}
            errors={errors}
            rules={{
              required: {
                value: true,
                message: 'Por favor, ingresá una descripción',
              },
              maxLength: { value: 256, message: 'Máximo 256 caracteres' },
            }}
            render={({ onChange, value }) => (
              <IonItem>
                <IonLabel position="floating">Descripción</IonLabel>
                <IonTextarea
                  disabled={!isTipoSelected}
                  rows={3}
                  value={capitalizeFirstLetter(value)}
                  maxlength={240}
                  placeholder={placeholder}
                  onIonChange={onChange}
                />
              </IonItem>
            )}
          />
        </div>

        <div className="ion-margin-top">
          <Button ref={ref} type="submit" disabled={isLoading || !isTipoSelected}>
            {!isLoading ? 'Enviar' : <Spinner color="light" />}
          </Button>
        </div>

        <Alert
          isOpen={alertState.isOpen}
          header={alertState.header}
          message={alertState.message}
          buttons={alertState.buttons}
          onDidDismiss={hideAlert}
        />
      </form>
    </div>
  );
};

export default React.memo(SiniestroForm);
